import * as React from "react";
import { getSupportUrl } from "../../utils/supportUrl";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { useNavigate } from "react-router-dom";
import { Autocomplete, TextField, Typography } from "@mui/material";
import { getCustomerWebhooks } from "./EditCameraUtils/getCustomerWebhooks";
import { getNumbersAfterSites } from "./EditCameraUtils/urlUtils";
import Box from "@mui/material/Box";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";

export const EditStreamWebhookDialog = ({
  setEditStreamWebhookDialogOpen,
  editStreamWebhookDialogOpen,
}: {
  setEditStreamWebhookDialogOpen: any;
  editStreamWebhookDialogOpen: any;
}) => {
  const { handlePatchStreamWebhook, setBackdropLoadingScreenEditCameraOn } =
    useEditCameraStore();

  const navigate = useNavigate();

  const selectedWebhook = JSON.parse(localStorage.selectedWebhook);

  const webHookEventTypeOptions = JSON.parse(
    localStorage.webHookEventTypeOptions
  );

  const webHookEventTypeNames = new Map();
  webHookEventTypeOptions.forEach((option: any) => {
    webHookEventTypeNames.set(option.name, option.id);
  });

  const webHookEventValues = new Map();
  webHookEventTypeOptions.forEach((option: any) => {
    webHookEventValues.set(option.value, option.id);
  });

  const [postObject, setPostObject] = React.useState({
    stream: selectedWebhook.stream.id,
    webhook: selectedWebhook.webhook.id,
    custom_fields: JSON.stringify(selectedWebhook.custom_fields, null, 2),
    event_types: [],
  });

  const [errorState, setErrorState] = React.useState({
    webhook: false,
    custom_fields: false,
  });

  const [customerWebhooks, setCustomerWebhooks] = React.useState<any[]>([]);
  const [webhooksLoaded, setWebhooksLoaded] = React.useState(false);

  React.useEffect(() => {
    getCustomerWebhooks(navigate, setCustomerWebhooks, () =>
      setWebhooksLoaded(true)
    );
    const updatedEventTypes: any[] = [];
    selectedWebhook.event_types.forEach((eventType: any) => {
      updatedEventTypes.push(webHookEventValues.get(eventType));
    });
    setPostObject((previousState: any) => {
      return {
        ...previousState,
        event_types: updatedEventTypes,
      };
    });
  }, []);

  return (
    <Dialog
      open={editStreamWebhookDialogOpen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ textAlign: "center" }}>
        Edit Stream Webhook{" "}
      </DialogTitle>
      <DialogContent>
        <FormLabel component="legend" sx={{ paddingBottom: 1 }}>
          {" "}
          Webhook*
        </FormLabel>
        {/* Options are the webhook objects, keyed by id: two site webhooks
            with the same display name must stay distinct (ENG-2233). Until
            the list loads, the saved webhook is shown from the row itself. */}
        <Autocomplete
          id="webhook"
          options={customerWebhooks}
          value={
            customerWebhooks.find(
              (candidate: any) => candidate.id === postObject.webhook
            ) ??
            (postObject.webhook === selectedWebhook.webhook.id
              ? selectedWebhook.webhook
              : null)
          }
          getOptionLabel={(option: any) => option?.display_name ?? ""}
          isOptionEqualToValue={(option: any, value: any) =>
            option?.id === value?.id
          }
          sx={{ width: 400 }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{ ...params.inputProps, "aria-label": "Webhook" }}
            />
          )}
          onChange={(event, value: any) => {
            setErrorState((previousState: any) => {
              return { ...previousState, webhook: value === null };
            });
            setPostObject((previousState: any) => {
              return {
                ...previousState,
                webhook: value ? value.id : null,
              };
            });
          }}
        />
        {webhooksLoaded && customerWebhooks.length === 0 && (
          <Typography
            variant="body2"
            sx={{ mt: 1, color: "#283E6D" }}
            data-testid="no-site-webhooks"
          >
            This site has no webhooks yet. Add one on the site&apos;s{" "}
            <a
              href={`/sites/aboutsite/${getNumbersAfterSites(location.href)}`}
              onClick={(event) => {
                event.preventDefault();
                navigate(
                  `/sites/aboutsite/${getNumbersAfterSites(location.href)}`
                );
              }}
              style={{ color: "#1976d2", textDecoration: "none" }}
            >
              Webhooks tab
            </a>
            , then come back to attach it to this camera.
          </Typography>
        )}
        <br></br>
        <Box sx={{ display: "flex" }}>
          <FormControl variant="standard">
            <FormLabel component="legend">Event Types</FormLabel>
            <FormGroup>
              {webHookEventTypeOptions.map((option: any) => {
                return (
                  <FormControlLabel
                    key={option.name}
                    control={
                      <Checkbox
                        //set default checked if the selected webhook has the event type
                        defaultChecked={selectedWebhook.event_types.includes(
                          option.value
                        )}
                        onChange={(event) => {
                          if (event.target.checked) {
                            setPostObject((previousState: any) => {
                              return {
                                ...previousState,
                                event_types: [
                                  ...previousState.event_types,
                                  webHookEventTypeNames.get(option.name),
                                ],
                              };
                            });
                          } else {
                            setPostObject((previousState: any) => {
                              return {
                                ...previousState,
                                event_types: previousState.event_types.filter(
                                  (eventType: any) =>
                                    eventType !==
                                    webHookEventTypeNames.get(option.name)
                                ),
                              };
                            });
                          }
                        }}
                        name={option.name}
                      />
                    }
                    label={option.name}
                  />
                );
              })}
            </FormGroup>
          </FormControl>
        </Box>
        <br></br>
        <FormLabel component="legend" sx={{ paddingBottom: 1 }}>
          {" "}
          Custom Fields
        </FormLabel>
        <Box>
          <TextField
            sx={{ width: 550 }}
            multiline
            rows={7}
            value={postObject.custom_fields}
            error={errorState.custom_fields}
            defaultValue={JSON.stringify(
              selectedWebhook.custom_fields,
              null,
              2
            )}
            onChange={(event) => {
              const inputValue = event.target.value;

              // Update postObject with the current input value
              setPostObject((previousState: any) => ({
                ...previousState,
                custom_fields: inputValue,
              }));

              // Validate JSON
              try {
                JSON.parse(inputValue);
                setErrorState((previousState: any) => ({
                  ...previousState,
                  custom_fields: false,
                }));
              } catch (e) {
                setErrorState((previousState: any) => ({
                  ...previousState,
                  custom_fields: true,
                }));
              }
            }}
            helperText={
              errorState.custom_fields
                ? "Invalid JSON format. Ensure your input is a valid JSON object."
                : ""
            }
          />

          <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
            Enter a valid JSON with custom fields that should be sent
            specifically for this camera. See formatting examples in our support
            page:{" "}
            <a
              href={getSupportUrl("/admin_ui/webhooks/")}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "none" }}
            >
              Webhooks
            </a>
          </Typography>
        </Box>
        <br></br>
        <br></br>
        <FormLabel component="legend"> *Required</FormLabel>
      </DialogContent>
      <DialogActions>
        <OrangeTextButton
          onClick={() => {
            setEditStreamWebhookDialogOpen(false);
          }}
          text="Cancel"
          disabled={false}
        />
        <OrangeTextButton
          onClick={() => {
            try {
              const parsedCustomFields = JSON.parse(postObject.custom_fields);

              // Prepare the postObject for API submission
              const payload = {
                ...postObject,
                custom_fields: parsedCustomFields, // Pass the parsed object
              };
              setBackdropLoadingScreenEditCameraOn(true);
              setEditStreamWebhookDialogOpen(false);
              handlePatchStreamWebhook(payload, selectedWebhook.id, navigate);
            } catch (error) {
              alert("Invalid JSON, cannot submit.");
            }
          }}
          text="Submit & Test"
          disabled={errorState.webhook || errorState.custom_fields}
        />
      </DialogActions>
    </Dialog>
  );
};
